import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function TabIntegrity({ project, reload }) {
  const [report, setReport] = useState(null);
  const [tocV2, setTocV2] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const [r, tv] = await Promise.all([
        api.get("/ph360/integrity"),
        api.get("/ph360/toc/validate"),
      ]);
      setReport(r.data);
      setTocV2(tv.data);
    } catch (err) { toast.error(formatApiError(err)); }
  }, []);
  useEffect(() => { load(); }, [load, project.updated_at]);

  const runRepairs = async () => {
    setBusy(true);
    try {
      await api.post("/ph360/integrity/apply-repairs");
      await load(); reload();
      toast.success("Safe repairs re-applied");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  if (!report) return <p>Running validation…</p>;
  const status = report.overall_status || "error";
  const badge = report.ready ? (report.warning_count ? "PASS WITH WARNINGS" : "READY TO PUBLISH") : "REVIEW REQUIRED";
  const metrics = [
    ["Chapters", report.chapter_count],
    ["Total Words", (report.total_words||0).toLocaleString()],
    ["Critical Errors", report.critical_count],
    ["Warnings", report.warning_count],
    ["Empty Chapters", report.empty_chapters],
    ["Missing Numbers", (report.missing_numbers||[]).length],
    ["Images", report.image_count],
    ["Lists", report.list_count],
  ];
  const v2Fixed = (project.structured?.chapters || []).filter(c => c._v2_repaired).length;
  const tocStatus = tocV2?.status || "pass";
  const modules = [
    ["Chapter Structure", status, "Sequential numbering, titles, body content, duplicates"],
    ["TOC (source)", report.toc_validation?.status || "pass", `Entries: ${report.toc_validation?.entry_count || 0}`],
    ["TOC v2 (rendered HTML cross-check)", tocStatus, `Rendered entries: ${tocV2?.rendered_count || 0}`],
    ["Heading Hierarchy", report.heading_validation?.status || "pass", `PART dividers: ${report.heading_validation?.part_count || 0}`],
    ["Front Matter", report.front_matter?.status || "pass", "Title page, copyright, publisher/disclosure settings"],
    ["Back Matter", report.back_matter?.status || "pass", "Author, imprint, closing-note settings"],
    ["Chapter Cleaner v2 (split-title repair)", v2Fixed > 0 ? "pass" : "pass", `${v2Fixed} title(s) repaired on last import`],
  ];
  const fp = project.manuscript_fingerprint || "(not yet computed)";
  const counts = project.object_counts || {};
  return (
    <>
      <h2>EF360 Structural Validation Engine™</h2>
      <p>Deterministic preflight validation for chapter sequence, heading boundaries, TOC integrity, manuscript content, and publishing matter.</p>
      <div className={`ef360-validation-hero ef360-status-${status}`} data-testid="integrity-hero">
        <div>
          <strong>{badge}</strong>
          <span>Import Confidence</span>
          <b>{report.confidence}%</b>
        </div>
      </div>
      <div className="ef360-metric-grid">
        {metrics.map(([label, val]) => (
          <div className="ef360-metric" key={label}>
            <span>{label}</span>
            <strong>{val}</strong>
          </div>
        ))}
      </div>
      <div className="notice notice-info" data-testid="integrity-ownership">
        <p style={{fontFamily:"Menlo, Consolas, monospace", fontSize:12}}>
          <strong>Source Object Fingerprint:</strong> {String(fp).slice(0, 32)}…
        </p>
        <p style={{fontSize:12,margin:"4px 0"}}>
          Ownership tree: <strong>{counts.book || 1}</strong> book · <strong>{counts.chapters || 0}</strong> chapters · <strong>{counts.parts || 0}</strong> parts · <strong>{counts.blocks || 0}</strong> blocks · <strong>{counts.back_sections || 0}</strong> back-sections
        </p>
      </div>
      {(report.issues || []).map((iss, i) => (
        <div className="notice notice-error" key={"e"+i}><p><strong>ERROR:</strong> {iss}</p></div>
      ))}
      {(report.warnings || []).map((w, i) => (
        <div className="notice notice-warning" key={"w"+i}><p><strong>WARNING:</strong> {w}</p></div>
      ))}
      {(tocV2?.errors || []).map((iss, i) => (
        <div className="notice notice-error" key={"tv-e"+i}><p><strong>TOC v2:</strong> {iss}</p></div>
      ))}
      {(tocV2?.warnings || []).map((iss, i) => (
        <div className="notice notice-warning" key={"tv-w"+i}><p><strong>TOC v2:</strong> {iss}</p></div>
      ))}
      {!report.rows?.length ? <p>No chapters detected. Import a manuscript first.</p> : (
        <>
          <h3>Validation Summary</h3>
          <table className="widefat striped">
            <thead><tr><th>Module</th><th>Status</th><th>Details</th></tr></thead>
            <tbody>{modules.map(([label, st, det]) => (
              <tr key={label}>
                <td><strong>{label}</strong></td>
                <td><span className={`ef360-status-pill ef360-status-${st}`}>{st==="pass"?"✓":st==="warning"?"⚠":"✕"} {st}</span></td>
                <td>{det}</td>
              </tr>
            ))}</tbody>
          </table>
          <h3>Chapter Preflight</h3>
          <table className="widefat striped" data-testid="integrity-preflight">
            <thead><tr><th>#</th><th>Verified Title</th><th>Subtitle</th><th>Words</th><th>¶</th><th>Lists</th><th>Media</th><th>Status</th></tr></thead>
            <tbody>{report.rows.map(row => {
              const st = row.status === "repaired" ? "pass" : row.status;
              const label = row.status==="pass"?"✓ Valid":row.status==="repaired"?"✓ Repaired":row.status==="warning"?"⚠ Warning":"✕ Error";
              return (
                <tr key={row.number}>
                  <td>{row.number}</td>
                  <td><strong>{row.title}</strong>{row.original_title && row.original_title !== row.title ? <><br/><small>Original: {row.original_title}</small></> : null}{row.notes?.length ? <><br/><small>{row.notes.join(" • ")}</small></> : null}</td>
                  <td>{row.subtitle || "—"}</td>
                  <td>{row.words.toLocaleString()}</td>
                  <td>{row.paragraphs}</td>
                  <td>{row.lists}</td>
                  <td>{row.images} img / {row.tables} tbl</td>
                  <td><span className={`ef360-status-pill ef360-status-${st}`}>{label}</span></td>
                </tr>
              );
            })}</tbody>
          </table>
        </>
      )}
      <p>
        <button className="button button-primary" onClick={runRepairs} disabled={busy} data-testid="integrity-run-repairs">
          {busy ? "Running…" : "Re-run Validation + Apply Safe Repairs"}
        </button>
      </p>
    </>
  );
}
